/**
 * SF-4665: "Fake data" panel on the company page. Lists the company's fake data batches and lets
 * a super admin remove one batch, or all non-legacy fake data. Shown only to holders of
 * admin:demos:create ("Create prospect demos"); the remove buttons also need the company to be
 * on the caller's list (the same edit gate sf-api applies). Hiding is defence in depth only.
 */
import { useEffect, useState } from 'react';
import { usePermissions } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Space, Table, Tag, Typography, message } from 'antd';
import { hasPermission } from '../../../utils/permissions';
import { DEMOS_PERMISSION } from '../../demos/constants';
import { useCanEditCompany } from '../api/editableCompaniesClient';
import {
  DEMO_BATCH_PAGE_SIZE,
  MODULE_LABELS,
  SOURCE_LABELS,
  STATUS_LABELS,
  batchesPollExpired,
  demoBatchesQueryKey,
  isBatchBusy,
  isBatchStuck,
  useDemoBatches,
  type DemoBatch,
} from '../api/demoDataClient';
import { RemoveAllModal, RemoveBatchModal } from './DemoDataRemoveModals';

const { Text } = Typography;

const STATUS_COLOURS: Record<DemoBatch['status'], string> = {
  planned: 'default',
  running: 'processing',
  done: 'success',
  failed: 'error',
  removing: 'processing',
  removed: 'default',
};

const formatDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString() : '—';

/** While a batch is still being added the final count is not known: show the planned one. */
const recordCount = (b: DemoBatch) =>
  (b.status === 'running' || b.status === 'planned' ? b.planned_total : b.created_total) ?? 0;

/**
 * Done or failed batches still hold records. Busy and removed ones cannot be removed, except a
 * batch stuck in planned/running for over 30 minutes (sf-api answers BATCH_BUSY if it is alive).
 */
const canRemove = (b: DemoBatch, now: number) =>
  b.status === 'done' || b.status === 'failed' || isBatchStuck(b, now);

const statusText = (b: DemoBatch) =>
  b.status === 'removed' && b.removed_at
    ? `${STATUS_LABELS.removed} ${formatDate(b.removed_at)}`
    : STATUS_LABELS[b.status] ?? b.status;

export const DemoDataSection: React.FC<{ companyId: string }> = ({ companyId }) => {
  const { data: permissions } = usePermissions<string[]>();
  const allowed = hasPermission(permissions ?? [], DEMOS_PERMISSION);
  const {
    canEditCompany,
    isLoading: scopeLoading,
    isError: scopeError,
    refetch: refetchScope,
  } = useCanEditCompany(companyId);
  const queryClient = useQueryClient();

  const [offset, setOffset] = useState(0);
  // When the current spell of busy polling began (time of the first busy read), or null.
  const [busySince, setBusySince] = useState<number | null>(null);

  // sf-api applies the company edit gate to the list too: off the caller's list, read nothing.
  const batchesQuery = useDemoBatches(companyId, offset, allowed && canEditCompany, busySince);
  const batches = batchesQuery.data?.data ?? [];
  const busy = batches.some(isBatchBusy);
  const lastReadAt = batchesQuery.dataUpdatedAt;

  useEffect(() => {
    if (busy && busySince === null) setBusySince(lastReadAt);
    if (!busy && busySince !== null) setBusySince(null);
  }, [busy, busySince, lastReadAt]);

  // Offset of a page past the first that came back empty, so the pager stops offering it.
  // Cleared by Refresh and by a remove, which may change what is there.
  const [emptyOffset, setEmptyOffset] = useState<number | null>(null);

  // A page past the first came back empty (e.g. exactly a full page of batches): step back, so
  // the admin is never stranded on an empty page with no pager.
  const pageEmpty = offset > 0 && !!batchesQuery.data && !batchesQuery.isFetching && batches.length === 0;
  useEffect(() => {
    if (!pageEmpty) return;
    setEmptyOffset(offset);
    setOffset(Math.max(0, offset - DEMO_BATCH_PAGE_SIZE));
  }, [pageEmpty, offset]);

  const [removing, setRemoving] = useState<DemoBatch | null>(null);
  const [removeAllOpen, setRemoveAllOpen] = useState(false);

  if (!allowed) return null;

  // The company list lookup failed: we do not know, so say so rather than "not on your list".
  if (scopeError) {
    return (
      <Card title="Fake data">
        <Space direction="vertical">
          <Alert type="error" showIcon message="Could not check your companies." />
          <Button size="small" onClick={() => void refetchScope()}>
            Refresh
          </Button>
        </Space>
      </Card>
    );
  }

  if (!scopeLoading && !canEditCompany) {
    return (
      <Card title="Fake data">
        <Text type="secondary">This company is not on your list, so its fake data is not shown.</Text>
      </Card>
    );
  }

  const now = Date.now();
  const pollStopped = busy && batchesPollExpired(busySince, now);
  const pageFull = batches.length >= DEMO_BATCH_PAGE_SIZE;
  const nextPageKnownEmpty = emptyOffset === offset + DEMO_BATCH_PAGE_SIZE;

  // Read-only: restart the bounded poll from now.
  const refresh = () => {
    setBusySince(Date.now());
    setEmptyOffset(null);
    void batchesQuery.refetch();
  };

  // A remove answers 202 and runs in the background: re-read the list now so the batch shows
  // "Removing" and the list keeps polling until it is removed. The poll window restarts from
  // now, as an earlier busy spell may already have used up its time limit.
  const afterRemove = () => {
    setBusySince(Date.now());
    setEmptyOffset(null);
    setRemoving(null);
    setRemoveAllOpen(false);
    message.success('Removing fake data. The list updates when it is done.');
    void queryClient.invalidateQueries({ queryKey: demoBatchesQueryKey(companyId) });
  };

  // Only one page is in hand. Past a full first page there may be removable batches out of
  // sight, so offer the button then too (the dialog reads the real counts; sf-api re-checks).
  const anyRemovable =
    offset > 0 || pageFull || batches.some((b) => canRemove(b, now) && !b.is_legacy);

  const columns = [
    {
      title: 'Module',
      key: 'module',
      render: (_: unknown, b: DemoBatch) => (
        <Space size={4}>
          {MODULE_LABELS[b.module] ?? b.module}
          {b.is_legacy ? <Tag color="orange">Legacy</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Dates',
      key: 'dates',
      render: (_: unknown, b: DemoBatch) =>
        b.params?.start_date && b.params?.end_date
          ? `${b.params.start_date} to ${b.params.end_date}`
          : '—',
    },
    { title: 'Records', key: 'records', render: (_: unknown, b: DemoBatch) => recordCount(b) },
    {
      title: 'Added by',
      key: 'by',
      render: (_: unknown, b: DemoBatch) => b.created_by?.name || b.created_by?.email || '—',
    },
    {
      title: 'From',
      key: 'source',
      render: (_: unknown, b: DemoBatch) => SOURCE_LABELS[b.source] ?? b.source,
    },
    { title: 'Added', key: 'created', render: (_: unknown, b: DemoBatch) => formatDate(b.created_at) },
    {
      title: 'Status',
      key: 'status',
      render: (_: unknown, b: DemoBatch) => (
        <Tag color={STATUS_COLOURS[b.status]}>{statusText(b)}</Tag>
      ),
    },
    {
      title: '',
      key: 'actions',
      render: (_: unknown, b: DemoBatch) =>
        canEditCompany && canRemove(b, now) ? (
          <Button size="small" danger onClick={() => setRemoving(b)}>
            Remove
          </Button>
        ) : null,
    },
  ];

  return (
    <Card
      title="Fake data"
      extra={
        canEditCompany && anyRemovable ? (
          <Button danger onClick={() => setRemoveAllOpen(true)}>
            Remove all fake data
          </Button>
        ) : null
      }
    >
      {/* Only a failed first read hides the table; a failed refresh keeps the last list. */}
      {batchesQuery.isError && !batchesQuery.data ? (
        <Alert type="error" showIcon message="Could not load the fake data batches." />
      ) : (
        <Table<DemoBatch>
          rowKey="id"
          size="small"
          loading={scopeLoading || batchesQuery.isLoading}
          dataSource={batches}
          columns={columns}
          // No total from sf-api through `custom`: offer the next page while this one is full,
          // unless that page has already come back empty.
          pagination={{
            current: offset / DEMO_BATCH_PAGE_SIZE + 1,
            pageSize: DEMO_BATCH_PAGE_SIZE,
            total: offset + batches.length + (pageFull && !nextPageKnownEmpty ? 1 : 0),
            showSizeChanger: false,
            hideOnSinglePage: true,
            onChange: (page) => setOffset((page - 1) * DEMO_BATCH_PAGE_SIZE),
          }}
          locale={{
            // Past page 1 an empty page only means "nothing more", never "no fake data".
            emptyText:
              scopeLoading || batchesQuery.isLoading || offset > 0
                ? ' '
                : 'No fake data for this company.',
          }}
        />
      )}
      {batchesQuery.isError && batchesQuery.data ? (
        <Text type="warning" style={{ display: 'block', marginTop: 8 }}>
          Couldn't refresh, showing the last list.
        </Text>
      ) : null}
      {busy && pollStopped ? (
        <Space style={{ display: 'flex', marginTop: 8 }}>
          <Text type="secondary">Fake data is being added or removed.</Text>
          <Button size="small" onClick={refresh}>
            Refresh
          </Button>
        </Space>
      ) : busy ? (
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          Fake data is being added or removed; this list refreshes by itself.
        </Text>
      ) : null}
      {removing ? (
        <RemoveBatchModal
          companyId={companyId}
          batch={removing}
          onClose={() => setRemoving(null)}
          onRemoved={afterRemove}
        />
      ) : null}
      {removeAllOpen ? (
        <RemoveAllModal
          companyId={companyId}
          onClose={() => setRemoveAllOpen(false)}
          onRemoved={afterRemove}
        />
      ) : null}
    </Card>
  );
};
